/**
 * File validation and normalization helper for Announcement uploads.
 * Ensures uploaded images strictly match JPEG/PNG/WEBP/GIF magic bytes
 * and documents match PDF/DOC/DOCX signatures, preventing invalid formats.
 */

export function formatFileSize(bytes) {
  if (!bytes || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Detects real file format from binary magic bytes.
 */
export async function detectFileTypeFromMagicBytes(file) {
  try {
    const slice = file.slice(0, 16);
    const buffer = await slice.arrayBuffer();
    const bytes = new Uint8Array(buffer);

    if (bytes.length < 4) return null;

    // JPEG: FF D8 FF
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      return { category: "IMAGE", mime: "image/jpeg", ext: "jpg" };
    }

    // PNG: 89 50 4E 47 0D 0A 1A 0A
    if (
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47
    ) {
      return { category: "IMAGE", mime: "image/png", ext: "png" };
    }

    // GIF: GIF87a or GIF89a (47 49 46 38)
    if (
      bytes[0] === 0x47 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x38
    ) {
      return { category: "IMAGE", mime: "image/gif", ext: "gif" };
    }

    // WEBP: RIFF (bytes 0-3) + WEBP (bytes 8-11)
    if (
      bytes.length >= 12 &&
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      return { category: "IMAGE", mime: "image/webp", ext: "webp" };
    }

    // PDF: %PDF (25 50 44 46)
    if (
      bytes[0] === 0x25 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x44 &&
      bytes[3] === 0x46
    ) {
      return { category: "PDF", mime: "application/pdf", ext: "pdf" };
    }

    // DOCX (ZIP-based): PK.. (50 4B 03 04)
    if (
      bytes[0] === 0x50 &&
      bytes[1] === 0x4b &&
      bytes[2] === 0x03 &&
      bytes[3] === 0x04
    ) {
      return {
        category: "DOC",
        mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ext: "docx",
      };
    }

    // Legacy DOC (OLE Compound File): D0 CF 11 E0
    if (
      bytes[0] === 0xd0 &&
      bytes[1] === 0xcf &&
      bytes[2] === 0x11 &&
      bytes[3] === 0xe0
    ) {
      return { category: "DOC", mime: "application/msword", ext: "doc" };
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Re-encodes any browser-supported image through HTML5 Canvas into standard JPEG bytes.
 */
export async function normalizeImageViaCanvas(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      try {
        const canvas = document.createElement("canvas");
        const maxDim = 2560;
        let width = img.width;
        let height = img.height;

        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Unable to encode image"));
              return;
            }
            const rawName = file.name || "image";
            const baseName = rawName.replace(/\.[^/.]+$/, "");
            const normalizedFile = new File([blob], `${baseName}.jpg`, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(normalizedFile);
          },
          "image/jpeg",
          0.92
        );
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to load image file"));
    };

    img.src = objectUrl;
  });
}

/**
 * Validates and normalizes any user-selected file for announcement upload.
 * Resolves with sanitized File object and metadata.
 */
export async function processFileForAnnouncement(file) {
  if (!file) throw new Error("No file provided");

  const detected = await detectFileTypeFromMagicBytes(file);
  const declaredName = file.name || "file";
  const declaredLower = declaredName.toLowerCase();
  const declaredIsImage =
    file.type?.startsWith("image/") ||
    /\.(jpe?g|png|webp|gif|bmp|svg|tiff|heic)$/i.test(declaredLower);
  const declaredIsDoc = /\.(pdf|doc|docx)$/i.test(declaredLower);

  let finalFile = file;
  let fileCategory = "IMAGE";

  if (detected?.category === "IMAGE") {
    fileCategory = "IMAGE";
    const baseName = declaredName.replace(/\.[^/.]+$/, "");
    const correctName = `${baseName}.${detected.ext}`;
    if (
      file.type !== detected.mime ||
      !declaredLower.endsWith(`.${detected.ext}`)
    ) {
      finalFile = new File([file], correctName, { type: detected.mime });
    }
  } else if (detected?.category === "PDF" || detected?.category === "DOC") {
    fileCategory = detected.category;
    const baseName = declaredName.replace(/\.[^/.]+$/, "");
    const correctName = `${baseName}.${detected.ext}`;
    if (
      file.type !== detected.mime ||
      !declaredLower.endsWith(`.${detected.ext}`)
    ) {
      finalFile = new File([file], correctName, { type: detected.mime });
    }
  } else if (declaredIsImage) {
    try {
      finalFile = await normalizeImageViaCanvas(file);
      fileCategory = "IMAGE";
    } catch {
      throw new Error(
        "Uploaded file content does not match standard image formats (JPEG, PNG, WEBP, GIF). Please select a valid image file."
      );
    }
  } else if (declaredIsDoc) {
    fileCategory = declaredLower.endsWith(".pdf") ? "PDF" : "DOC";
  } else {
    throw new Error(
      "Invalid file type. Allowed: Images (JPEG, PNG, WEBP, GIF) up to 5MB, or Documents (PDF, DOC, DOCX) up to 10MB."
    );
  }

  // Size constraints per backend specification
  if (fileCategory === "IMAGE" && finalFile.size > 5 * 1024 * 1024) {
    throw new Error("Image size must be less than 5MB");
  }
  if (
    (fileCategory === "PDF" || fileCategory === "DOC") &&
    finalFile.size > 10 * 1024 * 1024
  ) {
    throw new Error("Document size must be less than 10MB");
  }

  return {
    file: finalFile,
    category: fileCategory,
    name: finalFile.name,
    size: formatFileSize(finalFile.size),
  };
}
